import React from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import {
  CONFIG_PROVIDER_LOOKUP,
  COUNTRY_CODE,
  KYC_CHECK_OUTCOME,
  KYC_CHECK_STATUS,
  KYC_DOC_STATUS,
  KYC_PROVIDER,
  KYC_SUB_PROVIDER,
  NR_OB_COUNTRY,
  NROB_ACTIVE_STEPS,
  PRODUCT,
  USER_KYC_TIER_STATUS,
  VERIFICATIONS_ACTIVE_SEARCH
} from '@constants';
import { COUNTRY_ISO_CODES } from '@constants/v2/common';
import { AUTH_TOKEN_STORAGE_KEYS } from '@auth/constants';
import {
  formatDate,
  formatDateUtc,
  formatDateWithoutTime
} from '@utils/dateUtils';

export const getKycStatusColor = (kycStatus) => {
  if (!kycStatus) return null;
  if (kycStatus === 'COMPLETED' || kycStatus === 'COMPLETED_NEW')
    return 'brand.500';
  else if (kycStatus === 'E_KYC_REJECTED' || kycStatus === 'REJECTED')
    return 'red';
  else return 'yellow.500';
};

export const getKycV2StatusColor = (kycStatus) => {
  if (!kycStatus) return null;
  if (kycStatus === 'VERIFIED') return 'brand.500';
  else if (kycStatus === 'REJECTED' || kycStatus === 'BLOCKED') return 'red';
  else if (kycStatus === 'PENDING') return 'yellow.500';
  else return 'white';
};

export const getUserTierStatusColor = (tierStatus) => {
  if (!tierStatus) return 'gray.400';

  switch (tierStatus) {
    case USER_KYC_TIER_STATUS.AVAILABLE:
      return 'blue.400'; // Available but not required
    case USER_KYC_TIER_STATUS.REQUIRED:
      return 'yellow.400'; // Required to complete
    case USER_KYC_TIER_STATUS.ACTIVATION_FAILED:
      return 'red.500'; // Failed attempt
    case USER_KYC_TIER_STATUS.ACTIVE:
      return 'green.500'; // Successfully activated
    case USER_KYC_TIER_STATUS.EXHAUSTED:
      return 'orange.500'; // Limits exhausted
    case USER_KYC_TIER_STATUS.CLOSED:
      return 'gray.600'; // Moved to next tier
    case USER_KYC_TIER_STATUS.DISABLED:
      return 'gray.400'; // Disabled
    default:
      return 'white'; // fallback
  }
};

export const getCheckStatusColor = (checkStatus) => {
  if (!checkStatus) return null;
  if (checkStatus === KYC_CHECK_STATUS.COMPLETE) return 'brand.500';
  else if (checkStatus === KYC_CHECK_STATUS.FAILED) return 'red';
  else return 'yellow.500';
};

export const getCheckOutcomeColor = (checkOutcome) => {
  if (!checkOutcome) return 'white';
  if (checkOutcome === KYC_CHECK_OUTCOME.CLEAR) return 'brand.500';
  else return 'yellow.500';
};

export const getSourceAccountStatusColor = (status) => {
  if (status === 'ACTIVE') return 'brand.500';
  if (status === 'PENDING_VERIFICATION') return 'yellow.500';
  else return 'white';
};

export const getJobStatusColor = (status) => {
  if (status === 'CLOSED' || status === 'COMPLETED') return 'brand.500';
  if (status === 'FAILED') return 'red.500';
  else return 'white';
};

export const toUserData = (user) => ({
  ...user,
  createdAt: formatDate(user.createdAt),
  kycStatusColor: getKycStatusColor(user.kycStatus),
  updatedAt: formatDate(user.updatedAt)
});

export const toNrUserData = (user) => ({
  ...user,
  createdAt: formatDate(user.createdAt),
  nrStatusColor: getKycStatusColor(user.nrStatus), //todo
  updatedAt: formatDate(user.updatedAt)
});

export const toNrUserDetailsData = (user) => ({
  ...user
});

export const getOrderStatusColor = (orderStatus) => {
  switch (orderStatus) {
    case 'COMPLETED':
      return 'green';
    case 'PROCESSING':
      return 'blue';
    case 'PENDING':
      return 'yellow';
    case 'FAILED':
      return 'red';
    case 'REFUNDED':
      return 'orange';
    case 'CANCELLED':
      return 'gray';
    default:
      return 'gray';
  }
};

export const getTxnMonitoringStatusColor = (status) => {
  if (status === 'COMPLETED' || status === 'VERIFIED') return 'green';
  else if (status === 'FAILED' || status === 'REJECTED') return 'red';
  else return 'yellow.500';
};

export const getKycDocStatusColor = (status) => {
  if (status === KYC_DOC_STATUS.COMPLETED || status === KYC_DOC_STATUS.VERIFIED)
    return 'green';
  else if (status === KYC_DOC_STATUS.REJECTED) return 'red';
  else return 'yellow.500';
};

export const getUserKycStatusColor = (status) => {
  if (status === KYC_DOC_STATUS.COMPLETED || status === KYC_DOC_STATUS.VERIFIED)
    return 'green';
  else if (
    status === KYC_DOC_STATUS.REJECTED ||
    status === KYC_DOC_STATUS.BLOCKED
  )
    return 'red';
  else return 'yellow.500';
};

export const getRFIStatusColor = (status) => {
  if (status === 'COMPLETED') return 'green';
  else if (status === 'FAILED' || status === 'REJECTED') return 'red';
  else return 'yellow.500';
};

export const toErrorData = (error) => {
  if (!error) return null;

  let errorMessage;

  if (error.response?.data) {
    try {
      const errorData = JSON.parse(error.response.data);
      errorMessage = errorData.message;
    } catch (error) {
      console.log('Error parsing JSON:', error);
    }
  }

  return {
    status: error.response?.status || null,
    message: errorMessage || error.message || null,
    code: error.code || null,
    data: error.response?.data || null
  };
};

export const toOrderData = (orderData) =>
  !orderData
    ? null
    : {
        userId: orderData.userId,
        orderId: orderData.orderId,
        product: orderData.orderType,
        orderStatus: orderData.orderStatus,
        beneficiaryName: orderData.beneficiaryName,
        sourceAccount: orderData.sourceAccountName,
        user: orderData.userName || null,
        paymentMethod: orderData.paymentMethod || null,
        sendAmnt: orderData.sendAmount,
        receiveAmnt: orderData.receiveAmount,
        currencyFrom: orderData.currencyFrom,
        currencyTo: orderData.currencyTo,
        rate: orderData.transferRate,
        flatFee: orderData.flatFee,
        platformFee: orderData.platformFee,
        discount: orderData.discount,
        acquirer: orderData.acquirer,
        amount: orderData.amount,
        createdAt: formatDate(orderData.createdAt),
        metadata: toOrderMetadata(orderData.orderMetadata)
      };

export const toGomsOrderData = (orderData) => {
  if (!orderData) return null;

  const getFeeByLabel = (fees, label) => {
    if (!fees || !Array.isArray(fees)) return null;
    const fee = fees.find((f) => f.label === label);
    return fee ? fee.value : null;
  };

  const getTotalFees = (fees) => {
    if (!fees || !Array.isArray(fees)) return null;
    return fees.reduce((total, fee) => total + (fee.value || 0), 0);
  };

  const getRewardByType = (rewards, type) => {
    if (!rewards || !Array.isArray(rewards)) return null;
    const reward = rewards.find((r) => r.type === type);
    return reward ? reward.amount : null;
  };

  const getTotalDiscounts = (discounts) => {
    if (!discounts || !Array.isArray(discounts)) return null;
    return discounts.reduce(
      (total, discount) => total + (discount.amount || 0),
      0
    );
  };

  return {
    userId: orderData.userId,
    orderId: orderData.orderId,
    product: orderData.product,
    orderStatus: orderData.status,
    orderSubStatus: orderData.subStatus,
    amount: orderData.amount.amount,

    beneficiaryName:
      orderData?.additionalDetails?.beneficiaryDetail?.accountHolderName ||
      null,
    beneficiaryId:
      orderData?.additionalDetails?.beneficiaryDetail?.beneficiaryId || null,
    accountNumber:
      orderData?.additionalDetails?.beneficiaryDetail?.accountNumber || null,
    ifscCode: orderData?.additionalDetails?.beneficiaryDetail?.ifscCode || null,

    currency: orderData.amount.currency,
    payerName: orderData.payerDetails.payerName || null,
    payerUserId: orderData.payerDetails.payerId,
    user: orderData.payerDetails?.payerName || null,
    sendAmount: orderData.amount.amount,
    receiveAmnt: orderData?.additionalDetails?.receiveAmount?.amount || null,
    currencyFrom: orderData.amount.currency,
    currencyTo: orderData?.additionalDetails?.receiveAmount
      ? orderData?.additionalDetails?.receiveAmount.currency
      : null,

    rate:
      orderData.product === 'REMITTANCE'
        ? orderData.additionalDetails?.transferRate || null
        : null,

    transferFee:
      orderData.product === 'REMITTANCE'
        ? getFeeByLabel(orderData.additionalDetails?.fees, 'Transfer Fee')
        : null,
    platformFee:
      orderData.product === 'REMITTANCE'
        ? getFeeByLabel(orderData.additionalDetails?.fees, 'Platform Fee')
        : null,
    processingFee:
      orderData.product === 'REMITTANCE'
        ? getFeeByLabel(orderData.additionalDetails?.fees, 'Processing Fee')
        : null,
    totalFees:
      orderData.product === 'REMITTANCE'
        ? getTotalFees(orderData.additionalDetails?.fees)
        : null,

    discount:
      orderData.product === 'REMITTANCE'
        ? getTotalDiscounts(orderData.additionalDetails?.discounts)
        : null,
    discounts:
      orderData.product === 'REMITTANCE'
        ? orderData.additionalDetails?.discounts || []
        : [],

    vanceCash:
      orderData.product === 'REMITTANCE'
        ? getRewardByType(orderData.additionalDetails?.rewards, 'VANCE_CASH')
        : null,
    cashback:
      orderData.product === 'REMITTANCE'
        ? getRewardByType(orderData.additionalDetails?.rewards, 'CASHBACK')
        : null,
    rewards:
      orderData.product === 'REMITTANCE'
        ? orderData.additionalDetails?.rewards || []
        : [],

    complianceFlagged:
      orderData.product === 'REMITTANCE'
        ? orderData.additionalDetails?.complianceData?.complianceFlagged ||
          false
        : false,
    complianceFlagRemoved:
      orderData.product === 'REMITTANCE'
        ? orderData.additionalDetails?.complianceData?.complianceFlagRemoved ||
          false
        : false,

    acquirer:
      orderData.product === 'REMITTANCE'
        ? orderData.additionalDetails?.acquirer || null
        : null,
    fulfillmentProvider:
      orderData.product === 'REMITTANCE'
        ? orderData.additionalDetails?.fulfillmentProvider || null
        : null,
    previousState:
      orderData.product === 'REMITTANCE'
        ? orderData.additionalDetails?.previousState || null
        : null,

    createdAt: formatDate(orderData.createdAt),
    updatedAt: formatDate(orderData.updatedAt),

    additionalDetails: orderData.additionalDetails,

    metadata:
      orderData.product === 'REMITTANCE'
        ? toOrderMetadata(orderData.additionalDetails?.complianceData)
        : toOrderMetadata(orderData.orderMetadata)
  };
};

export const getBackwardCompatibleFees = (orderData) => {
  if (
    orderData.product !== 'REMITTANCE' ||
    !orderData.additionalDetails?.fees
  ) {
    return {
      flatFee: null,
      platformFee: null
    };
  }

  const fees = orderData.additionalDetails.fees;
  return {
    flatFee:
      getFeeByLabel(fees, 'Transfer Fee') || getFeeByLabel(fees, 'Flat Fee'),
    platformFee: getFeeByLabel(fees, 'Platform Fee')
  };
};

export const toOrderDataEnhanced = (orderData) => {
  const basicMapping = toOrderData(orderData);

  if (!basicMapping) return null;

  // Add enhanced fields for remittance orders
  if (orderData.product === 'REMITTANCE' && orderData.additionalDetails) {
    const additionalDetails = orderData.additionalDetails;

    return {
      ...basicMapping,

      // Enhanced beneficiary details
      beneficiaryDetails: {
        id: additionalDetails.beneficiaryDetail?.beneficiaryId,
        name: additionalDetails.beneficiaryDetail?.accountHolderName,
        accountNumber: additionalDetails.beneficiaryDetail?.accountNumber,
        ifscCode: additionalDetails.beneficiaryDetail?.ifscCode,
        bankName: additionalDetails.beneficiaryDetail?.bankName,
        bankCode: additionalDetails.beneficiaryDetail?.bankCode
      },

      // Enhanced fee breakdown
      feeBreakdown: {
        fees: additionalDetails.fees || [],
        totalFees: getTotalFees(additionalDetails.fees),
        currency: orderData.amount.currency
      },

      // Enhanced discount details
      discountDetails: {
        discounts: additionalDetails.discounts || [],
        totalDiscount: getTotalDiscounts(additionalDetails.discounts),
        currency: orderData.amount.currency
      },

      // Enhanced reward details
      rewardDetails: {
        rewards: additionalDetails.rewards || [],
        totalRewards:
          additionalDetails.rewards?.reduce(
            (total, reward) => total + (reward.amount || 0),
            0
          ) || 0,
        currency: orderData.amount.currency
      },

      // Enhanced compliance details
      complianceDetails: {
        flagged: additionalDetails.complianceData?.complianceFlagged || false,
        flagRemoved:
          additionalDetails.complianceData?.complianceFlagRemoved || false,
        updateBy: additionalDetails.complianceData?.updateByEmail || '',
        riskScore: additionalDetails.complianceData?.riskScore,
        verificationStatus: additionalDetails.complianceData?.verificationStatus
      }
    };
  }

  return basicMapping;
};

export const toLuluOrderData = (luluOrderData) =>
  !luluOrderData
    ? null
    : {
        userId: luluOrderData.userId,
        orderId: luluOrderData.orderId,
        status: luluOrderData.status,
        createdAt: formatDate(luluOrderData.createdAt),
        userName: luluOrderData.userName,
        transactionRefId: luluOrderData.transactionRefId,
        instrument: luluOrderData.instrument
      };

export const toCampaignData = (campaign) => ({
  id: campaign?.campaignId,
  code: campaign?.code,
  channel: campaign?.extras?.channel || campaign?.channel,
  countries: campaign?.extras?.csvAllowedCounties 
    ? campaign.extras.csvAllowedCounties.split(',').filter(Boolean)
    : [],
  link: campaign?.link,
  createdAt: campaign?.createdAt 
    ? formatDate(campaign.createdAt) 
    : null,
  updatedAt: campaign?.updatedAt 
    ? formatDate(campaign.updatedAt) 
    : null,
  campaignId: campaign?.campaignId,
  promoterId: campaign?.promoterId,
  template: campaign?.template
});

export const toOrderMetadata = (metadata) => ({
  ...metadata,
  updatedByEmail: metadata?.updateByEmail,
  createdAt: formatDate(metadata?.createdAt),
  updatedAt: formatDate(metadata?.updatedAt)
});

export const toHolidayData = (holiday) => {
  return {
    ...holiday,
    startTime: formatDateUtc(holiday.startTime),
    endTime: formatDateUtc(holiday.endTime),
    settlementDay: formatDateUtc(holiday.settlementDay)
  };
};

//check if a value is falsy, excluding 0
export const doesExist = (value) => value !== null && value !== undefined;

//for development
export const dummyApi = (response, error, timeout = 2000) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      if (response)
        return resolve({
          data: response
        });
      else return reject(error);
    }, timeout);
  });

export const initializeDeviceId = () => {
  let deviceId = localStorage.getItem(AUTH_TOKEN_STORAGE_KEYS.DEVICE_ID);
  if (deviceId) return;

  deviceId = uuidv4();

  localStorage.setItem(AUTH_TOKEN_STORAGE_KEYS.DEVICE_ID, deviceId);
};

export const toNrAccountOnboardingDetails = (onboarding) => ({
  ...onboarding
});

const isValidDate = (dateStr) => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}\+\d{2}:\d{2}$/;
  return dateRegex.test(dateStr);
};

export const toScreenDataList = (screenData) => ({
  ...Object.keys(screenData)
    .map((key) => ({
      key: camelCaseToSpaceSeparated(key),
      value:
        key === 'filePath'
          ? {
              value: 'Open File',
              isFile: true,
              fileUrl: screenData[key]
            }
          : isValidDate(screenData[key])
          ? {
              value: formatDate(screenData[key])
            }
          : {
              value: screenData[key]
            }
    }))
    .reduce(
      (prev, curr) => ({
        ...prev,
        [curr.key]: curr.value
      }),
      {}
    )
});

export const toWorkflowScreenDataList = (screenData) => {
  const fileFields = screenData?.documentsList
    ? Object.keys(screenData.documentsList)
        .map((key) => ({
          key: camelCaseToSpaceSeparated(key),
          value: {
            value: 'Open File',
            isFile: true,
            fileUrl: screenData.documentsList[key]
          }
        }))
        .reduce(
          (prev, curr) => ({
            ...prev,
            [curr.key]: curr.value
          }),
          {}
        )
    : {};

  let res = !screenData
    ? {}
    : {
        ...Object.keys(screenData)
          .map((key) => ({
            key: camelCaseToSpaceSeparated(key),
            value: isValidDate(screenData[key])
              ? {
                  value: formatDate(screenData[key])
                }
              : {
                  value: screenData[key]
                }
          }))
          .reduce(
            (prev, curr) => ({
              ...prev,
              [curr.key]: curr.value
            }),
            {}
          )
      };

  delete res['Documents List'];

  res = {
    ...res,
    ...fileFields
  };
  return res;
};

/* camelcase to space seperated word, considering abbreviated words like PANCard */
export const camelCaseToSpaceSeparated = (text) => {
  if (!text?.length) return text;
  let arr = [],
    lastCap = 0;

  for (let i = 0; i < text.length; i++) {
    if (
      text[i] === text[i].toLowerCase() &&
      i >= 1 &&
      text[i - 1] === text[i - 1].toUpperCase()
    ) {
      arr.push(text.slice(lastCap, i - 1));
      lastCap = i - 1;
    }
  }
  arr.push(text.slice(lastCap));
  arr[0] = arr[0].slice(0, 1).toUpperCase() + arr[0].slice(1);

  return arr.join(' ');
};

export const kebabCaseToSpaceSeperate = (text) => {
  if (!text) return text;
  return text.split('_').join(' ');
};

export const toSentenceCase = (str) => {
  if (!str) return str;
  return str.toLowerCase().replace(/^(.)|\s(.)/g, function (match) {
    return match.toUpperCase();
  });
};

// Helper to convert camelCase to Sentence Case (e.g., "createdAt" -> "Created At")
export const camelToSentenceCase = (str) => {
  if (!str) return str;
  return str
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (char) => char.toUpperCase()) // Capitalize first letter
    .trim();
};

export function isObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const isLowerCase = (l) => l.toUpperCase() !== l && l.toLowerCase() === l;
const isUpperCase = (l) => l.toUpperCase() === l && l.toLowerCase() !== l;

export const camelizeObjectKeys = (obj) => {
  let resObj = {};

  Object.keys(obj).forEach((key) => {
    if (!isObject(obj[key])) {
      resObj = {
        ...resObj,
        [camelizeString(key)]: obj[key] //todo: update with camelized string
      };
    } else {
      resObj = {
        ...resObj,
        [camelizeString(key)]: camelizeObjectKeys(obj[key]) //todo update key
      };
    }
  });

  return resObj;
};

const camelizeString = (str) => {
  let wordStartInd = 0;
  const arr = [];
  for (let i = 1; i < str.length; i++) {
    if (isUpperCase(str[i]) && isLowerCase(str[i - 1])) {
      arr.push(str.slice(wordStartInd, i));
      wordStartInd = i;
    } else if (str[i] === '_') {
      arr.push(str.slice(wordStartInd, i));
      wordStartInd = i + 1;
    } else if (
      isUpperCase(str[i]) &&
      isUpperCase(str[i - 1]) &&
      i + 1 < str.length &&
      isLowerCase(str[i + 1])
    ) {
      arr.push(str.slice(wordStartInd, i));
      wordStartInd = i;
    }
  }

  if (str.slice(wordStartInd)?.length) arr.push(str.slice(wordStartInd));

  let res = '';

  for (let i = 0; i < arr.length; i++) {
    if (isUpperCase(arr[i])) {
      let j = i;

      while (j < arr.length && isUpperCase(arr[j])) {
        j++;
      }

      res = res + arr.slice(i, j).join('_');
      i = j - 1;
    } else {
      res =
        res +
        (i === 0 ? arr[i] : arr[i].slice(0, 1).toUpperCase() + arr[i].slice(1));
    }
  }

  return res;
};

export const getDefaultProduct = () => PRODUCT.REMITTANCE;

export const getDefaultKycProviderForProduct = (product) => {
  switch (product) {
    case PRODUCT.REMITTANCE:
      return KYC_PROVIDER.COMPLY_CUBE;
    default:
      return null;
  }
};

export const getDefaultKycSubProviderForKycProvider = () =>
  KYC_SUB_PROVIDER.FULL_KYC_TIER;

export const getDefaulProductForKycProvider = (provider) => {
  switch (provider) {
    default:
      return PRODUCT.REMITTANCE;
  }
};

export const getErrorMessage = ({
  error,
  genericMessage = 'Something went wrong!'
}) =>
  error?.data
    ? JSON.parse(error.data)?.message
    : error.message || genericMessage;

export const getProductsForActiveVerificationSearch = (activeSearch) => {
  switch (activeSearch) {
    case VERIFICATIONS_ACTIVE_SEARCH.REMITTANCE:
      return [PRODUCT.REMITTANCE];
    default:
      return [];
  }
};

export const getProvidersForActiveVerificationSearch = (activeSearch) => {
  switch (activeSearch) {
    case VERIFICATIONS_ACTIVE_SEARCH.REMITTANCE:
      return [
        KYC_PROVIDER.COMPLY_CUBE,
        KYC_PROVIDER.EFR,
        KYC_PROVIDER.SUMSUB,
        KYC_PROVIDER.PERSONA
        // KYC_PROVIDER.ENCRYPTUS
      ];
    default:
      return [];
  }
};

export const getTextForProduct = (product) => {
  switch (product) {
    case PRODUCT.REMITTANCE:
      return 'Remittance';
    default:
      return '';
  }
};

export const getTextForProvider = (provider) => {
  switch (provider) {
    case KYC_PROVIDER.COMPLY_CUBE:
      return 'Comply Cube';
    case KYC_PROVIDER.EFR:
      return 'EFR';
    case KYC_PROVIDER.TARRAKKI:
      return 'Tarrakki';
    case KYC_PROVIDER.ENCRYPTUS:
      return 'Encryptus';
    case KYC_PROVIDER.SUMSUB:
      return 'Sumsub';
    case KYC_PROVIDER.PERSONA:
      return 'Persona';
    default:
      return '';
  }
};

export const getVerificationTitle = (product, provider) => {
  switch (`${product}:${provider}`) {
    case `${PRODUCT.REMITTANCE}:${KYC_PROVIDER.RFX}`:
      return 'RFX Kyc';
    case `${PRODUCT.REMITTANCE}:${KYC_PROVIDER.COMPLY_CUBE}`:
      return 'Comply Cube Kyc';
    case `${PRODUCT.REMITTANCE}:${KYC_PROVIDER.ON_RAMP_MONEY}`:
      return 'On Ramp Money Kyc';
    case `${PRODUCT.REMITTANCE}:${KYC_PROVIDER.VANCE}`:
      return 'Vance Kyc';
    case `${PRODUCT.REMITTANCE}:${KYC_PROVIDER.PERSONA}`:
      return 'Persona Kyc';
    default:
      return '';
  }
};

export const isPlainObject = (value) => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

export const mapJobsReqData = (searchReqData) => {
  return {
    ...searchReqData,
    filters: Object.keys(searchReqData.filters)
      .map((filterKey) => ({
        filterKey: filterKey,
        values: searchReqData.filters[filterKey]?.length
          ? [searchReqData.filters[filterKey]]
          : null
      }))
      .filter((filter) => filter.values !== null)
  };
};

export const formatToInputDisplayDate = (dateString) => {
  if (!dateString) return '';
  let dateObj = new Date(dateString);

  try {
    var dateOptions = {
      timeZone: 'UTC',
      minute: 'numeric',
      hour: 'numeric',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    dateObj = dateObj.toLocaleString('en-US', dateOptions);
  } catch (error) {
    return '';
  }
  dateObj = new Date(dateString);
  let year = dateObj.getFullYear();
  year = '0'.repeat(4 - year.toString().length) + year;
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export const formatFromInputDisplayDate = (dateString) => {
  if (dateString === '') return '';
  const dateTime = dateString + 'T00:00';
  return new Date(dateTime + 'Z').toISOString();
};

export const getInputDisplayDate = (date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const toWorkflowScreenDataListV2 = (screenData) => {
  const { uploadedDocs = null, screenId, ...screen } = screenData;

  const result = Object.keys(screen)
    .map((key) => ({
      key: camelCaseToSpaceSeparated(key),
      value: screen[key]
    }))
    .reduce((prev, curr) => {
      return {
        ...prev,
        [curr.key]: {
          value: curr.value
        }
      };
    }, {});

  if (!uploadedDocs) return result;

  const files = Object.keys(uploadedDocs)
    .map((key) => ({
      key: camelCaseToSpaceSeparated(key),
      value: {
        isFile: true,
        value: 'Open File',
        fileUrl: uploadedDocs[key]
      }
    }))
    .reduce((prev, curr) => {
      return {
        ...prev,
        [curr.key]: curr.value
      };
    }, {});

  return { ...result, ...files };
};

export function camelCaseKeys(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(camelCaseKeys);
  }

  return Object.keys(obj).reduce((acc, key) => {
    // Preserve keys that are ALL CAPS or contain underscores (e.g. US, IE, OTHERS, SURGE_PRIORITY)
    if (/^[A-Z0-9_]+$/.test(key)) {
      acc[key] = camelCaseKeys(obj[key]);
      return acc;
    }

    if (key.split('_').length === 1) {
      acc[key] = camelCaseKeys(obj[key]);
      return acc;
    }
    const camelKey = key
      .toLowerCase()
      .replace(/[_-]\w/g, (match) => match[1].toUpperCase())
      .replace(/[-_]/g, '');
    acc[camelKey] = camelCaseKeys(obj[key]);
    return acc;
  }, {});
}

export function decamelizeAndUpperCase(str) {
  return str.replace(/([a-z\d])([A-Z])/g, '$1_$2').toUpperCase();
}

export function camelCaseString(str) {
  return str
    .toLowerCase()
    .replace(/[_-]\w/g, (match) => match[1].toUpperCase())
    .replace(/[-_]/g, '');
}

export function transformObjectKeysToUpperCaseWith_(obj) {
  if (Array.isArray(obj)) {
    return obj.map(transformObjectKeysToUpperCaseWith_);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const newKey = decamelizeAndUpperCase(key);
      acc[newKey] = transformObjectKeysToUpperCaseWith_(obj[key]);
      return acc;
    }, {});
  }
  return obj;
}

export function transformToNrobFunnelReqData({
  query = null,
  pageNo = 0,
  pageSize = 20,
  /* regular filters */
  filters = {
    country: null
  },
  /* additional filters */
  additionalFilters = null
}) {
  let countryValues = [];
  // TODO: create a mapper for each filter
  const { country, funnelState, funnelSubstate } = filters;
  if (!country) countryValues = [NR_OB_COUNTRY.AE, NR_OB_COUNTRY.GB];
  else if (country === NR_OB_COUNTRY.AE) countryValues = [NR_OB_COUNTRY.AE];
  else if (country === NR_OB_COUNTRY.GB) countryValues = [NR_OB_COUNTRY.GB];

  const hasAdditionalFilters = !additionalFilters
    ? false
    : Object.values(additionalFilters).reduce((prev, curr) => {
        return prev && curr;
      }, true);

  function getFunnelStateFilter(activeStep, subStateVal) {
    if (!subStateVal) return null;
    switch (activeStep) {
      case NROB_ACTIVE_STEPS.APPLICATION_PROCESSING:
        return {
          filter_key: 'APPLICATION_FILLING_STATUS',
          operation: 'EQUALS',
          values: [subStateVal]
        };
      default:
        return null;
    }
  }

  const funnelSubStateFilter = getFunnelStateFilter(
    funnelState,
    funnelSubstate
  );

  const reqFilters = !funnelSubStateFilter
    ? [
        {
          filter_key: 'COUNTRY',
          search_operation: 'EQUALS',
          values: countryValues
        },
        {
          filter_key: 'ACTIVE_STEP',
          search_operation: 'EQUALS',
          values: [funnelState]
        }
      ]
    : [
        {
          filter_key: 'COUNTRY',
          search_operation: 'EQUALS',
          values: countryValues
        },
        {
          filter_key: 'ACTIVE_STEP',
          search_operation: 'EQUALS',
          values: [funnelState]
        },
        {
          ...funnelSubStateFilter
        }
      ];

  return {
    query,
    pageNo,
    pageSize,
    filters: reqFilters,
    ...(hasAdditionalFilters ? { additionalFilters } : {})
  };
}

export const getRejectReasonsBasedOnDoc = (docType) => {
  switch (docType) {
    case 'BANK_STATEMENT':
      return ['Date out of range', 'Not a valid bank statement'];
    case 'PASSPORT_FRONT':
    case 'PASSPORT_BACK':
      return [
        'Incorrect document provided',
        'Passport information not visible (Blurry or unclear image)',
        'Expired passport',
        'Tampered passport',
        'Expiry date is less than 6 months',
        'Uploaded a screenshot'
      ];
    case 'BRP_FRONT':
    case 'BRP_BACK':
      return [
        'Blurry or unclear image',
        'BRP information not visible (Blurry or unclear image)',
        'Expired BRP',
        'Uploaded invalid document',
        'Expiry date is less than 6 months',
        'Uploaded a screenshot'
      ];
    case 'SIGNATURE':
    case 'SELFIE':
      return [
        'Poor image quality (blurry or low resolution)',
        'Signature not matching with passport',
        'Face not clearly visible or obscured',
        'Selfie does not match the photo in the provided ID document',
        'Invalid Selfie'
      ];
    case 'EMIRATES_ID':
      return [
        'Expired Emirates ID',
        'Invalid document provided',
        'Blurry or unclear Image',
        'Expiry date is less than 6 months'
      ];
    default:
      return [];
  }
};

export function configCountryCodeLookup(countryCode) {
  const lookup = Object.keys(CONFIG_PROVIDER_LOOKUP).reduce((prev, curr) => {
    const providers = CONFIG_PROVIDER_LOOKUP[curr];

    if (providers.length === 0)
      return {
        ...prev,
        [curr]: {
          code: curr,
          provider: null
        }
      };

    const providerTags = providers.reduce((prev, provider) => {
      return {
        ...prev,
        [`${curr}_${provider}`]: {
          code: curr,
          provider
        }
      };
    }, {});

    return {
      ...prev,
      [curr]: {
        code: curr,
        provider: null
      },
      ...providerTags
    };
  }, {});

  return lookup[countryCode];
}

export function transformCountryCodesForConfig(countries) {
  return countries
    ?.map((country) => {
      if (CONFIG_PROVIDER_LOOKUP[country].length === 0)
        return [
          {
            country,
            provider: null
          }
        ];
      return [
        ...CONFIG_PROVIDER_LOOKUP[country]?.map((provider) => {
          return { country, provider };
        }),
        { country, provider: null }
      ];
    })
    .reduce((prev, curr) => {
      return [...prev, ...curr];
    }, []);
}

export function convertHoursToHoursAndMinutes(hours) {
  // Split the hours into integer and fractional parts
  var integerHours = Math.floor(hours);
  var fractionalHours = hours - integerHours;

  // Calculate minutes from the fractional part
  var minutes = Math.round(fractionalHours * 60);

  // Return formatted result
  return integerHours + ' hrs ' + minutes + ' minutes';
}

export function getCountryFromCode(countryCode) {
  switch (countryCode) {
    case COUNTRY_CODE['+44']:
      return `${COUNTRY_ISO_CODES.GB} - United Kingdom`;
    case COUNTRY_CODE['+971']:
      return `${COUNTRY_ISO_CODES.AE} - United Arab Emarites`;
    case COUNTRY_CODE['+49']:
      return `${COUNTRY_ISO_CODES.DE} - Germany`;
    case COUNTRY_CODE['+33']:
      return `${COUNTRY_ISO_CODES.FR} - France`;
    case COUNTRY_CODE['+31']:
      return `${COUNTRY_ISO_CODES.NL} - Netherlands`;
    case COUNTRY_CODE['+351']:
      return `${COUNTRY_ISO_CODES.PT} - Portugal`;
    case COUNTRY_CODE['+34']:
      return `${COUNTRY_ISO_CODES.ES} - Spain`;
    case COUNTRY_CODE['+353']:
      return `${COUNTRY_ISO_CODES.IE} - Ireland`;
    case COUNTRY_CODE['+39']:
      return `${COUNTRY_ISO_CODES.IT} - Italy`;
    default:
      return '-';
  }
}

export const getCountryNameFromCountryISOCode = (shorthand) => {
  switch (shorthand) {
    case COUNTRY_ISO_CODES.GB:
      return `United Kingdom`;
    case COUNTRY_ISO_CODES.AE:
      return `United Arab Emirates`;
    case COUNTRY_ISO_CODES.DE:
      return `Germany`;
    case COUNTRY_ISO_CODES.FR:
      return `France`;
    case COUNTRY_ISO_CODES.NL:
      return `Netherlands`;
    case COUNTRY_ISO_CODES.PT:
      return `Portugal`;
    case COUNTRY_ISO_CODES.ES:
      return `Spain`;
    case COUNTRY_ISO_CODES.IE:
      return `Ireland`;
    case COUNTRY_ISO_CODES.IT:
      return `Italy`;
    case COUNTRY_ISO_CODES.US:
      return `United States of America`;
    case COUNTRY_ISO_CODES.IN:
      return `India`;
    case COUNTRY_ISO_CODES.SG:
      return 'Singapore'
    default:
      return '-';
  }
};

export const getParsedObj = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

export const getNumberFormatBasedOnCountry = (country) => {
  switch (country.toUpperCase()) {
    case 'INDIA':
      return {
        formatCode: 'en-IN',
        currency: 'INR'
      };
    case 'UK':
      return {
        formatCode: 'en-US',
        currency: 'GBP'
      };
  }
};

export const formatNumberWithCommas = (number, precision = 2) => {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  });
  return formatter.format(number);
};

export const formatNumberToInr = (value) => {
  return Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(value);
};

/**
 * Formats a number as currency using the provided ISO currency code.
 * @param {Object} params
 * @param {string} params.currencyISOCode - The ISO currency code (e.g., 'INR', 'GBP').
 * @param {number} params.value - The numeric value to format.
 * @returns {string} The formatted currency string.
 */
export function formatAmountWithCurrency({ currencyISOCode, value }) {
  if (typeof value !== 'number' || !currencyISOCode) return 'N/A';
  // Pick locale based on currency for best formatting
  let locale = 'en-US';
  if (currencyISOCode === 'INR') locale = 'en-IN';
  // Add more currency/locale mappings here as needed
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyISOCode
  }).format(value);
}

/**
 * Convert snake_case to camelCase
 * @param {string} str - String in snake_case
 * @returns {string} - String in camelCase
 */
export const snakeToCamel = (str) => {
  return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
};

/**
 * RecursiveObjectRenderer - A component that renders nested objects and arrays in a readable format
 *
 * @param {any} value - The value to render (can be object, array, or primitive)
 * @param {number} level - The nesting level (used for indentation)
 */
export const RecursiveObjectRenderer = ({ value, level = 0 }) => {
  if (value === null || value === undefined) {
    return <Text fontSize="sm" color="whiteAlpha.700">-</Text>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Text fontSize="sm" color="whiteAlpha.700">[]</Text>;
    }
    return (
      <VStack align="stretch" spacing={2} pl={level > 0 ? 3 : 0}>
        {value.map((item, index) => (
          <Box
            key={index}
            bg="blackAlpha.300"
            p={2}
            borderRadius="md"
            borderLeft="2px solid"
            borderLeftColor="brand.500"
          >
            {typeof item === 'object' && item !== null ? (
              <RecursiveObjectRenderer value={item} level={level + 1} />
            ) : (
              <Text fontSize="sm" color="whiteAlpha.900">{item?.toString() || '-'}</Text>
            )}
          </Box>
        ))}
      </VStack>
    );
  }

  if (typeof value === 'object') {
    return (
      <VStack align="stretch" spacing={2} pl={level > 0 ? 3 : 0}>
        {Object.entries(value).map(([nestedKey, nestedValue]) => (
          <Box key={nestedKey}>
            <Text fontSize="xs" color="whiteAlpha.500" mb={1}>
              {camelToSentenceCase(nestedKey)}:
            </Text>
            {typeof nestedValue === 'object' && nestedValue !== null ? (
              <RecursiveObjectRenderer value={nestedValue} level={level + 1} />
            ) : (
              <Text fontSize="sm" color="whiteAlpha.900" pl={1}>
                {nestedValue?.toString() || '-'}
              </Text>
            )}
          </Box>
        ))}
      </VStack>
    );
  }

  return <Text fontSize="sm" color="whiteAlpha.900">{value?.toString() || '-'}</Text>;
};
